import IntentLandingLayout from "@/components/landing/IntentLandingLayout";

export default function AppelsManquesAgenceImmobiliere() {
  return (
    <IntentLandingLayout
      canonical="/appels-manques-agence-immobiliere"
      title="Appels manqués en agence immobilière : mesurer et agir"
      description="Appels manqués en agence immobilière : mesurez où ils se produisent, estimez leur impact commercial et organisez un relais pour ne plus laisser sonner dans le vide."
      eyebrow="Appels manqués agence immobilière"
      h1="Un appel manqué ne devrait pas devenir un mandat manqué."
      lede="Les appels manqués en agence immobilière arrivent pendant les visites, les rendez-vous et les pics d’activité. Immovox prend le relais avant que la demande ne reste sans contexte."
      serviceType="Réduction des appels manqués en agence immobilière"
      primaryCtaLabel="Récupérer mes appels manqués"
      acquisitionHero={{
        visual: "missed",
        visualLabel: "Appels manqués agence immobilière",
        visualTitle: "Un appel manqué ne devrait pas devenir une opportunité perdue.",
        visualText: "Le bon objectif n’est pas seulement de décrocher : c’est de conserver assez de contexte pour reprendre la demande efficacement.",
        secondaryCtaLabel: "Voir comment ça marche",
        strip: ["Repérer quand les appels sont perdus", "Déclencher un relais sur non-réponse", "Récupérer le motif et le contexte avant rappel"],
      }}
      definition={<>Un <strong>appel manqué en agence immobilière</strong> n'est pas automatiquement un lead perdu, mais il crée un risque : l'appelant doit attendre, rappeler ou contacter une autre agence. La priorité consiste à mesurer ces appels puis à prévoir une réponse ou un relais adapté.</>}
      sections={[
        {
          title: "Pourquoi les appels sont-ils manqués en agence ?",
          bullets: [
            "Les négociateurs sont en visite, en estimation ou en déplacement.",
            "Plusieurs appels arrivent alors qu'une seule personne gère le standard.",
            "Les annonces locatives créent des pics courts et difficiles à absorber.",
            "Les appels arrivent avant l'ouverture, après la fermeture ou le week-end.",
            "L'appel est transféré vers une personne indisponible sans scénario de repli.",
          ],
        },
        {
          title: "Commencer par mesurer plutôt que supposer",
          intro: "Un audit simple sur quelques semaines suffit souvent à faire apparaître les créneaux et motifs problématiques.",
          bullets: [
            "Nombre d'appels entrants et nombre d'appels non répondus.",
            "Jour et heure des appels manqués.",
            "Temps avant abandon lorsque la donnée est disponible.",
            "Part des appels rappelés et délai moyen avant rappel.",
            "Motif de l'appel lorsque celui-ci peut être reconstitué.",
          ],
        },
        {
          title: "Comment estimer l'impact commercial d'un appel manqué",
          paragraphs: [
            "Évitez les statistiques génériques qui ne correspondent pas à votre agence. Utilisez vos propres données.",
            "Une formule simple consiste à partir du nombre d'appels manqués, puis à estimer la part réellement commerciale, le taux de transformation habituel de ces demandes et la valeur moyenne d'une conversion. Le résultat donne un ordre de grandeur propre à votre activité, pas une promesse universelle.",
          ],
        },
        {
          title: "Quatre façons de réduire les appels perdus",
          bullets: [
            "Faire sonner plusieurs collaborateurs ou extensions lorsque c'est pertinent.",
            "Mettre en place un renvoi sur non-réponse après quelques sonneries.",
            "Organiser une permanence sur les plages où l'agence est habituellement indisponible.",
            "Utiliser un assistant IA pour décrocher, qualifier et préparer le rappel plutôt que basculer directement sur messagerie.",
          ],
        },
        {
          title: "Le bon objectif : ne pas seulement décrocher, mais préparer la suite",
          paragraphs: [
            "Un appel peut être techniquement « répondu » et rester peu exploitable si l'équipe reçoit seulement un nom et un numéro.",
            "Pour les demandes commerciales, le relais doit permettre de savoir pourquoi la personne appelle, quel bien ou projet est concerné et quelles informations ont déjà été recueillies. C'est cette continuité qui réduit réellement la friction au rappel.",
          ],
        },
      ]}
      faq={[
        { question: "Comment connaître mon taux d'appels manqués ?", answer: "Comparez les appels entrants avec les appels décrochés sur une période représentative. Votre opérateur ou standard peut généralement fournir l'historique nécessaire." },
        { question: "Un appel manqué est-il forcément perdu ?", answer: "Non. Certains appelants rappellent et l'agence peut rappeler rapidement. Le risque augmente surtout lorsque le délai s'allonge ou que l'appelant contacte plusieurs professionnels." },
        { question: "Quelle solution pour les appels manqués pendant les visites ?", answer: "Un renvoi sur non-réponse vers une permanence humaine ou un assistant IA permet de traiter l'appel sans interrompre la visite." },
        { question: "Faut-il répondre 24h/24 ?", answer: "Pas nécessairement. Commencez par les plages où vos données montrent un volume utile d'appels non traités." },
        { question: "Que doit collecter l'IA sur un appel manqué ?", answer: "Au minimum le motif, les coordonnées utiles et le contexte métier nécessaire au rappel. Le détail dépend du type d'appelant." },
      ]}
      related={[
        { to: "/permanence-telephonique-immobilier", label: "Permanence téléphonique", description: "Créer un relais sur les plages où les appels sont perdus." },
        { to: "/standard-telephonique-immobilier", label: "Standard téléphonique IA", description: "Répondre et qualifier au lieu de basculer sur messagerie." },
        { to: "/blog/appels-manques-agence-immobiliere-impact-ia", label: "Coût d'un appel manqué", description: "Une méthode pour estimer l'impact avec vos propres données." },
      ]}
      ctaTitle="Traiter les appels que votre agence manque aujourd'hui"
      ctaText="Testez Immovox sur un renvoi de débordement et mesurez le nombre de demandes réellement récupérées avec un contexte exploitable."
    />
  );
}
